// pages/support/tickets/CategoriesPage.tsx
import { useState } from "react";
import { Plus, Search, Edit, Trash2, X, Tag, PlusCircle } from "lucide-react";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import PageListLayout from "../../../layouts/PageListLayout";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import useProblemCategories from "../../../hooks/features/tickets/useProblemCategories";

export default function CategoriesPage() {
  const { 
    categories = [], 
    loading, 
    fetchCategories, 
    createCategory, 
    updateCategory, 
    deleteCategory 
  } = useProblemCategories();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  // États pour l'ajout rapide
  const [bulkInput, setBulkInput] = useState("");
  const [singleInput, setSingleInput] = useState("");
  const [isAddingBulk, setIsAddingBulk] = useState(false);
  const [isAddingSingle, setIsAddingSingle] = useState(false);

  const filteredCategories = categories.filter((cat) =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cat.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Ajout en masse (séparé par ; ou ,)
  const handleBulkAdd = async () => {
    if (!bulkInput.trim()) {
      alertError("Veuillez saisir au moins une categorie");
      return;
    }

    setIsAddingBulk(true);
    try {
      const names = bulkInput
        .split(/[;,]/)
        .map(name => name.trim())
        .filter(name => name.length > 0);

      if (names.length === 0) {
        alertError("Aucune categorie valide");
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (const name of names) {
        try {
          await createCategory({ name });
          successCount++;
        } catch (error) {
          errorCount++;
        }
      }

      if (errorCount === 0) {
        alertSuccess(`${successCount} categorie(s) creee(s) avec succes`);
      } else {
        alertSuccess(`${successCount} categorie(s) creee(s), ${errorCount} erreur(s)`);
      }

      setBulkInput("");
      fetchCategories();
    } catch (error: any) {
      const message = error.response?.data?.message || "Erreur lors de l'ajout";
      alertError(message);
    } finally {
      setIsAddingBulk(false);
    }
  };

  // Ajout simple (une par une)
  const handleSingleAdd = async () => {
    if (!singleInput.trim()) {
      alertError("Veuillez saisir un nom de categorie");
      return;
    }

    setIsAddingSingle(true);
    try {
      await createCategory({ name: singleInput.trim() });
      alertSuccess("Categorie creee avec succes");
      setSingleInput("");
      fetchCategories();
    } catch (error: any) {
      const message = error.response?.data?.message || "Erreur lors de la creation";
      alertError(message);
    } finally {
      setIsAddingSingle(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alertError("Le nom de la categorie est requis");
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description || null,
      };

      if (editingCategory) {
        await updateCategory(editingCategory.id, payload);
        alertSuccess("Categorie modifiee avec succes");
      } else {
        await createCategory(payload);
        alertSuccess("Categorie creee avec succes");
      }

      setIsModalOpen(false);
      setEditingCategory(null);
      setFormData({ name: "", description: "" });
      fetchCategories();
    } catch (error: any) {
      const message = error.response?.data?.message || "Erreur lors de l'enregistrement";
      alertError(message);
    }
  };

  const handleDelete = async () => {
    if (!categoryToDelete) return;
    setIsDeleting(true);
    try {
      const deleted =await deleteCategory(categoryToDelete.id);
     console.log("deleted" , deleted)
      alertSuccess("Categorie supprimee avec succes");
      setCategoryToDelete(null);
      fetchCategories();
    } catch (error: any) {
      const message = error.response?.data?.message || "Erreur lors de la suppression";
      alertError(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    setFormData({ name: "", description: "" });
    setIsModalOpen(true);
  };

  const openEditModal = (category: any) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || "",
    });
    setIsModalOpen(true);
  };


  console.log("categories" , categories)

  return (
    <PageListLayout title="">
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Categories de problemes</h1>
              <p className="text-sm text-gray-500 mt-1">Gerez les categories pour organiser vos tickets</p>
            </div>
            <button
              onClick={openCreateModal}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
            >
              <Plus size={18} />
              Nouvelle categorie
            </button>
          </div>

          <div className="relative mt-6">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une categorie..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50"
            />
          </div>
        </div>
      </div>

      <div className="px-6 py-8 max-w-7xl mx-auto">
        {/* Section d'ajout rapide */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* Ajout en masse */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Ajout en masse
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Separez les categories par ; ou ,
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
                placeholder="Ex: Probleme technique; Facturation; Livraison"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                onKeyPress={(e) => e.key === 'Enter' && handleBulkAdd()}
              />
              <button
                onClick={handleBulkAdd}
                disabled={isAddingBulk}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {isAddingBulk ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  "Ajouter"
                )}
              </button>
            </div>
          </div>

          {/* Ajout simple */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Ajout simple
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Ajoutez une categorie une par une
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={singleInput}
                onChange={(e) => setSingleInput(e.target.value)}
                placeholder="Ex: Probleme de connexion"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                onKeyPress={(e) => e.key === 'Enter' && handleSingleAdd()}
              />
              <button
                onClick={handleSingleAdd}
                disabled={isAddingSingle}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 whitespace-nowrap flex items-center gap-1"
              >
                {isAddingSingle ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <PlusCircle size={16} />
                    Ajouter
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Liste des categories */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="text-center py-12">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Tag size={32} className="text-primary/60" />
              </div>
            </div>
            <p className="text-gray-500">
              {searchTerm ? "Aucune categorie correspondante" : "Aucune categorie creee"}
            </p>
            <button
              onClick={openCreateModal}
              className="mt-4 text-primary hover:text-primary/80 font-medium"
            >
              Creer votre premiere categorie
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCategories.map((category) => (
              <div
                key={category.id}
                className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">{category.name}</h3>
                    {category.description && (
                      <p className="text-sm text-gray-500 mt-0.5">{category.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => openEditModal(category)}
                      className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                      title="Modifier"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => setCategoryToDelete(category)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de creation/edition */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingCategory ? "Modifier la categorie" : "Nouvelle categorie"}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de la categorie <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Ex: Probleme technique, Facturation..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Description de la categorie..."
                />
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-medium"
                >
                  {editingCategory ? "Modifier" : "Creer"}
                </button>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de suppression */}
      <DeleteConfirmationModal
        isOpen={!!categoryToDelete}
        onClose={() => setCategoryToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer la categorie"
        message={
          <div>
            <p>Supprimer la categorie <strong>"{categoryToDelete?.name}"</strong> ?</p>
          </div>
        }
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />
    </PageListLayout>
  );
}